import { secondsToMilliseconds } from "date-fns";
import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import useScreenShareCallbacks from "../../session/controls/useScreenShareCallbacks";
import {
  isWhiteboardModeAtom,
  patientConsentAtom,
  providerConsentAtom,
} from "../../session/messages/state";
import { resolveCorrectConsentState } from "../../session/messages/utils";
import { toast } from "../../toasts";
import { UserType } from "../../types";
import {
  ConsentStatus,
  DataChannelAppState,
  DataChannelAppStateMessage,
  DataChannelAppStateProperty,
  DataChannelMessageSender,
  DataChannelUpdateStatus,
  recordingStateAtom,
} from "../../video-sdk";
import { ConsentDeclinedToast } from "../session-recording/ConsentDeclinedToast";
import { otherPatientConsentedInSessionAtom } from "../session-recording/state";

function useHandleRecordingUpdateCallback() {
  const setRecordingState = useSetAtom(recordingStateAtom);

  return useCallback(
    (receivedState: DataChannelAppState) => {
      setRecordingState(receivedState.recordingState);
    },
    [setRecordingState],
  );
}

function useHandleIsWhiteboardModeUpdateCallback() {
  const { stopScreenShare } = useScreenShareCallbacks();
  const setIsWhiteboardMode = useSetAtom(isWhiteboardModeAtom);

  return useCallback(
    (receivedState: DataChannelAppState) => {
      setIsWhiteboardMode(receivedState.isWhiteboardMode);
      if (receivedState.isWhiteboardMode.value) {
        stopScreenShare();
      }
    },
    [setIsWhiteboardMode, stopScreenShare],
  );
}

function useHandleMultiParticipantConsentCallback() {
  const setOtherPatientConsentedInSession = useSetAtom(
    otherPatientConsentedInSessionAtom,
  );

  return useCallback(
    (correctConsentState: DataChannelAppStateProperty<ConsentStatus>) => {
      /** Notifies the user if another patient declined consent */
      if (correctConsentState.value === ConsentStatus.SOFT_NO) {
        toast.enqueue(
          (ariaProps) => (
            <ConsentDeclinedToast
              {...ariaProps}
              visitorDeclinedConsent={false}
            />
          ),
          { timeout: secondsToMilliseconds(30) },
        );
      }
      /** Notifies the user if another patient consented */
      if (correctConsentState.value === ConsentStatus.OPTED_IN) {
        setOtherPatientConsentedInSession(true);
      }
    },
    [setOtherPatientConsentedInSession],
  );
}

function useHandlePatientConsentUpdateCallback() {
  const getCurrentPatientConsent = useAtomCallback(
    useCallback((get) => get(patientConsentAtom), []),
  );

  const setPatientConsent = useSetAtom(patientConsentAtom);

  const handleMultiParticipantConsent =
    useHandleMultiParticipantConsentCallback();

  return useCallback(
    (
      receivedParticipantState: DataChannelAppState,
      sender: DataChannelMessageSender,
    ) => {
      const currentConsentState = getCurrentPatientConsent();
      const [correctConsentState, comparisonResult] =
        resolveCorrectConsentState({
          receivedState: receivedParticipantState.patient.consent,
          userState: currentConsentState,
        });

      switch (comparisonResult) {
        case DataChannelUpdateStatus.LAST_UPDATED_CHANGED:
          setPatientConsent(correctConsentState);
          return;
        case DataChannelUpdateStatus.VALUE_AND_LAST_UPDATED_CHANGED:
          setPatientConsent(correctConsentState);
          if (sender.entityType === UserType.CLIENT) {
            handleMultiParticipantConsent(correctConsentState);
          }
          return;
        case DataChannelUpdateStatus.NO_UPDATE_NEEDED:
        default:
          return;
      }
    },
    [
      getCurrentPatientConsent,
      handleMultiParticipantConsent,
      setPatientConsent,
    ],
  );
}

export function useHandleProviderConsentUpdateCallback() {
  const setProviderConsent = useSetAtom(providerConsentAtom);

  return useCallback(
    (receivedParticipantState: DataChannelAppState) => {
      setProviderConsent(receivedParticipantState.provider.consent);
    },
    [setProviderConsent],
  );
}

export function useUpdateDataChannelAppStateCallback() {
  const handlePatientConsentUpdate = useHandlePatientConsentUpdateCallback();
  const handleProviderConsentUpdate = useHandleProviderConsentUpdateCallback();
  const handleRecordingUpdate = useHandleRecordingUpdateCallback();
  const handleIsWhiteboardModeUpdate =
    useHandleIsWhiteboardModeUpdateCallback();

  return useCallback(
    (message: DataChannelAppStateMessage) => {
      const { participantState, sender } = message;
      handlePatientConsentUpdate(participantState, sender);
      handleProviderConsentUpdate(participantState);
      handleRecordingUpdate(participantState);
      handleIsWhiteboardModeUpdate(participantState);
    },
    [
      handlePatientConsentUpdate,
      handleRecordingUpdate,
      handleProviderConsentUpdate,
      handleIsWhiteboardModeUpdate,
    ],
  );
}
